import {h, render, ComponentChildren} from 'preact'
import {useState, useMemo, useEffect, useCallback} from 'preact/hooks'

type CarInfo = {
    name: string
    transmissionType: string
}

type TeamUser = {
    name: string
    image: string
    isOffice: boolean
    isTeacher: boolean
    isInstructor: boolean
    locations: string
    shortDescription?: string
    shortSentence?: string
    certificationSince?: string
    languages?: string[]
    cars?: CarInfo[]
    teacherCategories?: string[]
    calendarNotes?: string | null
}

type Category = 'office' | 'instructors' | 'teachers'

const categories: { key: Category; label: string }[] = [
    {key: 'office', label: 'Office'},
    {key: 'instructors', label: 'Instruktor:innen'},
    {key: 'teachers', label: 'Fahrlehrer:innen'},
]

function matchesCategory(user: TeamUser, selected: Set<Category>): boolean {
    if (selected.has('office') && user.isOffice) return true
    if (selected.has('teachers') && user.isTeacher) return true
    if (selected.has('instructors') && !user.isTeacher && user.isInstructor) return true
    return false
}

function getRoleLabel(user: TeamUser): string {
    if (user.isOffice) return 'Office'
    if (user.isTeacher) return 'Fahrlehrer:in'
    if (user.isInstructor) return 'Instruktor:in'
    return ''
}

function getRoleBadgeClass(user: TeamUser): string {
    if (user.isTeacher) return 'bg-blue-100 text-blue-800'
    if (user.isInstructor) return 'bg-amber-100 text-amber-800'
    return 'bg-gray-100 text-gray-600'
}

function RoleBadge({user}: { user: TeamUser }) {
    const label = getRoleLabel(user)
    if (!label) return null
    return (
        <span className={`self-start rounded-full px-4 py-1 text-base font-semibold ${getRoleBadgeClass(user)}`}>
            {label}
        </span>
    )
}

const SRCSET_WIDTHS = [400, 800, 1200, 1800]

function cloudinarySrcSet(url: string): string | undefined {
    if (!url.includes('/image/upload/')) return undefined
    return SRCSET_WIDTHS
        .map(w => `${url.replace('/image/upload/', `/image/upload/w_${w},`)} ${w}w`)
        .join(', ')
}

function seededRandom(seed: number): () => number {
    let s = seed >>> 0
    return () => {
        s = Math.imul(s, 1664525) + 1013904223 >>> 0
        return s / 0x100000000
    }
}

function seededShuffle<T>(array: T[], seed: number): T[] {
    const shuffled = [...array]
    const rand = seededRandom(seed)
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
}

function readSeedFromUrl(): number | null {
    const hash = window.location.hash.slice(1)
    const n = parseInt(hash, 10)
    return Number.isFinite(n) ? n : null
}

function writeSeedToUrl(seed: number): void {
    history.replaceState(null, '', '#' + seed)
}

function randomSeed(): number {
    return Math.floor(Math.random() * 0x100000000)
}


function SteckbriefItem({icon, children}: { icon: ComponentChildren; children: ComponentChildren }) {
    return (
        <div className="flex items-center space-x-3">
            <div className="rounded-full bg-gray-100 p-2 shrink-0 text-gray-600">
                {icon}
            </div>
            <p>{children}</p>
        </div>
    )
}

function CalendarIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 fill-current" viewBox="0 0 256 256">
            <path
                d="M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32Zm0,176H48V48H72v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V208Zm-68.69-90.34a8,8,0,0,1,0,11.31l-48,48a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L85.66,160l42.34-42.34A8,8,0,0,1,139.31,117.66Z"/>
        </svg>
    )
}

function ChatIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 fill-current" viewBox="0 0 256 256">
            <path
                d="M216,48H40A16,16,0,0,0,24,64V224a15.85,15.85,0,0,0,9.24,14.5A16.13,16.13,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78l.09-.07L83,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM216,192H80a8,8,0,0,0-5.23,1.95L40,224V64H216Z"/>
        </svg>
    )
}

function MapPinIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 fill-current" viewBox="0 0 256 256">
            <path
                d="M128,64a40,40,0,1,0,40,40A40,40,0,0,0,128,64Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,128,128Zm0-112a88.1,88.1,0,0,0-88,88c0,31.4,14.51,64.68,42,96.25a254.19,254.19,0,0,0,41.45,38.3,8,8,0,0,0,9.18,0A254.19,254.19,0,0,0,174,200.25c27.45-31.57,42-64.85,42-96.25A88.1,88.1,0,0,0,128,16Zm0,206c-16.53-13-72-60.75-72-118a72,72,0,0,1,144,0C200,161.23,144.53,209,128,222Z"/>
        </svg>
    )
}

function CarIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 fill-current" viewBox="0 0 256 256">
            <path
                d="M240,112H229.2L201.42,49.5A16,16,0,0,0,186.8,40H69.2a16,16,0,0,0-14.62,9.5L26.8,112H16a8,8,0,0,0,0,16h8v80a16,16,0,0,0,16,16H64a16,16,0,0,0,16-16V192h96v16a16,16,0,0,0,16,16h24a16,16,0,0,0,16-16V128h8a8,8,0,0,0,0-16ZM69.2,56H186.8l24.89,56H44.31ZM64,208H40V192H64Zm128,0V192h24v16Zm24-32H40V128H216Z"/>
        </svg>
    )
}

function StarIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" className="size-5 fill-current" viewBox="0 0 256 256">
            <path
                d="M239.18,97.26A16.38,16.38,0,0,0,224.92,86l-59-4.76L143.14,26.15a16.36,16.36,0,0,0-30.27,0L90.11,81.23,31.08,86a16.46,16.46,0,0,0-9.37,28.86l45,38.83L53,211.75a16.38,16.38,0,0,0,24.5,17.82L128,198.49l50.53,31.08A16.38,16.38,0,0,0,203,211.75l-13.76-58.07,45-38.83A16.38,16.38,0,0,0,239.18,97.26Zm-15.34,5.47-48.7,42a8,8,0,0,0-2.56,7.91l14.88,62.79a.37.37,0,0,1-.17.48c-.18.14-.23.11-.38,0l-54.72-33.65a8,8,0,0,0-8.38,0L69.09,215.94c-.15.09-.19.12-.38,0a.37.37,0,0,1-.17-.48l14.88-62.79a8,8,0,0,0-2.56-7.91l-48.7-42c-.12-.1-.23-.19-.13-.5s.18-.27.33-.29l63.92-5.16A8,8,0,0,0,103,91.86l24.62-59.6c.08-.17.11-.25.35-.25s.27.08.35.25l24.62,59.6a8,8,0,0,0,6.67,4.88l63.92,5.16c.15,0,.24,0,.33.29S224,102.63,223.84,102.73Z"/>
        </svg>
    )
}

function TeacherResult({user}: { user: TeamUser }) {
    const hasTransmissionInfo = user.cars && user.cars.length > 0
    const carNames = user.cars?.map(c => c.name).join(', ') ?? ''
    const hasManual = user.cars?.some(c => c.transmissionType === 'manual')

    return (
        <div className="flex flex-col text-black px-6 py-6 w-full">
            <p className="text-3xl font-bold">{user.name}</p>
            <div className="mt-2">
                {user.shortDescription
                    ? <span className={`self-start rounded-full px-4 py-1 text-base font-semibold ${getRoleBadgeClass(user)}`}>{user.shortDescription}</span>
                    : <RoleBadge user={user}/>
                }
            </div>
            {user.shortSentence && (
                <p className="mt-3 text-sm italic text-gray-500">{user.shortSentence}</p>
            )}

            <div className="mt-5 space-y-2.5 text-sm">
                {user.certificationSince && (
                    <SteckbriefItem icon={<CalendarIcon/>}>
                        Fahrlehrer:in seit {user.certificationSince}
                    </SteckbriefItem>
                )}
                {user.languages && user.languages.length > 0 && (
                    <SteckbriefItem icon={<ChatIcon/>}>
                        {user.languages.join(', ')}
                    </SteckbriefItem>
                )}
                {user.locations && (
                    <SteckbriefItem icon={<MapPinIcon/>}>
                        {user.locations}
                    </SteckbriefItem>
                )}
                {hasTransmissionInfo && (
                    <SteckbriefItem icon={<CarIcon/>}>
                        {carNames}{hasManual ? ', Automat & Geschalten' : ', Automat'}
                    </SteckbriefItem>
                )}
                {user.teacherCategories && user.teacherCategories.length > 0 && (
                    <SteckbriefItem icon={<StarIcon/>}>
                        Unterrichtskategorien: {user.teacherCategories.join(', ')}
                    </SteckbriefItem>
                )}
                {user.calendarNotes && (
                    <SteckbriefItem icon={<ChatIcon/>}>
                        {user.calendarNotes}
                    </SteckbriefItem>
                )}
            </div>
        </div>
    )
}

function SimpleResult({user}: { user: TeamUser }) {
    return (
        <div className="flex flex-col text-black px-6 py-6">
            <p className="text-3xl font-bold">{user.name}</p>
            <div className="mt-2">
                <RoleBadge user={user}/>
            </div>
            {user.locations && (
                <p className="mt-1 text-sm text-gray-500">{user.locations}</p>
            )}
        </div>
    )
}

function TeamCards({allUsers}: { allUsers: TeamUser[] }) {
    const [selectedCategories, setSelectedCategories] = useState<Set<Category>>(
        new Set(['teachers', 'office', 'instructors'])
    )
    const [currentIndex, setCurrentIndex] = useState(0)
    const [showResult, setShowResult] = useState(false)
    const [seed, setSeed] = useState<number>(() => {
        const fromUrl = readSeedFromUrl()
        if (fromUrl !== null) {
            return fromUrl
        }
        const fresh = randomSeed()
        writeSeedToUrl(fresh)
        return fresh
    })

    const users = useMemo(() => {
        const filtered = allUsers.filter(u => matchesCategory(u, selectedCategories))
        return seededShuffle(filtered, seed)
    }, [allUsers, selectedCategories, seed])

    const user = users[currentIndex]

    const toggleCategory = useCallback((cat: Category) => {
        setSelectedCategories(prev => {
            const next = new Set(prev)
            if (next.has(cat)) {
                next.delete(cat)
            } else {
                next.add(cat)
            }
            return next
        })
        setCurrentIndex(0)
        setShowResult(false)
        const newSeed = randomSeed()
        writeSeedToUrl(newSeed)
        setSeed(newSeed)
    }, [])

    const next = useCallback(() => {
        if (!showResult) {
            setShowResult(true)
            return
        }
        setShowResult(false)
        setCurrentIndex(i => (i + 1) >= users.length ? 0 : i + 1)
    }, [showResult, users.length])

    const previous = useCallback(() => {
        if (showResult) {
            setShowResult(false)
            return
        }
        setShowResult(true)
        setCurrentIndex(i => (i - 1) < 0 ? users.length - 1 : i - 1)
    }, [showResult, users.length])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') next()
            if (e.key === 'ArrowLeft') previous()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [next, previous])

    if (users.length === 0) {
        return (
            <div className="py-10 flex justify-center flex-col gap-4 items-center">
                <p className="text-gray-500">Keine Teammitglieder gefunden.</p>
            </div>
        )
    }

    return (
        <div className="py-10 flex justify-center flex-col gap-4 items-center">
            <div className="max-w-4xl w-full">
                <div className="sm:hidden">
                    <label className="sr-only">Kategorie wählen</label>
                    <select
                        className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        multiple
                        value={[...selectedCategories]}
                        onChange={(e) => {
                            const options = (e.target as HTMLSelectElement).selectedOptions
                            const selected = new Set<Category>(
                                Array.from(options, o => o.value as Category)
                            )
                            setSelectedCategories(selected)
                            setCurrentIndex(0)
                            setShowResult(false)
                            const newSeed = randomSeed()
                            writeSeedToUrl(newSeed)
                            setSeed(newSeed)
                        }}
                    >
                        {categories.map(c => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                    </select>
                </div>
                <div className="hidden sm:block">
                    <nav className="flex space-x-4" aria-label="Tabs">
                        {categories.map(c => (
                            <button
                                key={c.key}
                                type="button"
                                onClick={() => toggleCategory(c.key)}
                                className={`rounded-md px-3 py-2 text-sm font-medium ${
                                    selectedCategories.has(c.key)
                                        ? 'bg-gray-100 text-gray-700'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div className="max-w-4xl w-full">
                <div className="rounded-md overflow-hidden aspect-[3/2] relative">
                    {!showResult ? (
                        <>
                            <img
                                src={user.image}
                                srcSet={cloudinarySrcSet(user.image)}
                                sizes="(min-width: 896px) 896px, 100vw"
                                className="w-full h-full object-cover"
                                alt="Foto von Mitarbeiter"
                            />
                            <div
                                className="absolute inset-0 flex items-stretch justify-between bg-linear-to-r from-black/30 via-black/0 to-black/30">
                                <button
                                    type="button"
                                    onClick={previous}
                                    className="text-white hover:bg-linear-to-l hover:from-black/0 hover:to-black/10 transition-colors z-10 px-2"
                                >
                                    <span className="sr-only">Zurück</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 fill-current"
                                         viewBox="0 0 256 256">
                                        <path
                                            d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={next}
                                    className="text-white hover:bg-linear-to-r hover:from-black/0 hover:to-black/10 transition-colors z-10 px-2"
                                >
                                    <span className="sr-only">Weiter</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 fill-current"
                                         viewBox="0 0 256 256">
                                        <path
                                            d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/>
                                    </svg>
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex bg-white h-full">
                            <img
                                src={user.image}
                                srcSet={cloudinarySrcSet(user.image)}
                                sizes="(min-width: 896px) 299px, 33vw"
                                className="w-1/3 h-full object-cover"
                                alt="Foto von Mitarbeiter"
                            />
                            <div className="flex-1 min-w-0 overflow-y-auto">
                                {user.isTeacher
                                    ? <TeacherResult user={user}/>
                                    : <SimpleResult user={user}/>
                                }
                            </div>
                            <button
                                type="button"
                                onClick={next}
                                className="absolute right-0 top-0 bottom-0 text-gray-400 hover:bg-gray-100 transition-colors z-10 px-2"
                            >
                                <span className="sr-only">Weiter</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 fill-current"
                                     viewBox="0 0 256 256">
                                    <path
                                        d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const el = document.getElementById('team-cards-app')
if (el) {
    const users: TeamUser[] = JSON.parse(el.dataset.users || '[]')
    render(<TeamCards allUsers={users}/>, el)
}
